import { apiService } from './apiService';
import { lookupService } from './lookupService';
import { User, UserCreate, UserUpdate, Role, Location } from '../types';

export const userService = {
  async getUsers(params?: { skip?: number; limit?: number; active_only?: boolean; location_id?: number }): Promise<User[]> {
    return apiService.get<User[]>('/admin/', params);
  },

  async getUserById(userId: number): Promise<User> {
    return apiService.get<User>(`/admin/users/${userId}`);
  },

  async createUser(
    userData: UserCreate,
    options?: { notify_user?: boolean }
  ): Promise<{ user: User; notification_sent: boolean }> {
    const params = options?.notify_user !== false ? { notify_user: true } : { notify_user: false };
    return apiService.post<{ user: User; notification_sent: boolean }>('/admin/users/', userData, { params });
  },

  async updateUser(userId: number, userData: UserUpdate): Promise<User> {
    return apiService.put<User>(`/admin/users/${userId}`, userData);
  },

  async deactivateUser(userId: number): Promise<{ message: string }> {
    return apiService.delete<{ message: string }>(`/admin/users/${userId}`);
  },

  async deleteUserPermanent(userId: number): Promise<{ message: string }> {
    return apiService.delete<{ message: string }>(`/admin/users/${userId}/permanent`);
  },

  /** Roles — via bootstrap when available, else GET /admin/roles/ */
  async getRoles(params?: { skip?: number; limit?: number; forceRefresh?: boolean }): Promise<Role[]> {
    return lookupService.getRoles(params);
  },

  /** Locations — via bootstrap when available, else GET /locations/ */
  async getLocations(
    params?: { skip?: number; limit?: number; active_only?: boolean },
    options?: { forceRefresh?: boolean },
  ): Promise<Location[]> {
    return lookupService.getLocations({ ...params, forceRefresh: options?.forceRefresh });
  },

  async getEmailStatus(): Promise<{ email_configured: boolean; message: string }> {
    return apiService.get<{ email_configured: boolean; message: string }>('/admin/email-status');
  },

  async getUserPermissions(userId: number): Promise<{
    user_id: number;
    role_permissions: string[];
    grants: string[];
    denies: string[];
    effective_permissions: string[];
  }> {
    return apiService.get(`/admin/users/${userId}/permissions`);
  },

  async updateUserPermissions(
    userId: number,
    payload: { grants: string[]; denies: string[] }
  ): Promise<{
    user_id: number;
    role_permissions: string[];
    grants: string[];
    denies: string[];
    effective_permissions: string[];
  }> {
    return apiService.put(`/admin/users/${userId}/permissions`, payload);
  },

  async resetUserPassword(
    userId: number,
    payload: { new_password?: string } = {}
  ): Promise<{ message: string }> {
    return apiService.put(`/admin/users/${userId}/reset-password`, payload);
  },
};

export default userService;
